import React, { useState, useEffect } from 'react';

const DateOfBirthSelector = ({ value, onChange, id, errors = {} }) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [selectedDecade, setSelectedDecade] = useState(null);

  // Parse the existing value on component mount
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const yearVal = date.getFullYear().toString();
        setDay(date.getDate().toString().padStart(2, '0'));
        setMonth((date.getMonth() + 1).toString().padStart(2, '0'));
        setYear(yearVal);
        // Set the decade based on the year
        setSelectedDecade(Math.floor(date.getFullYear() / 10) * 10);
      }
    }
  }, [value]);

  // Update parent component when date parts change
  useEffect(() => {
    if (day && month && year) {
      const dateString = `${year}-${month}-${day}`;
      const date = new Date(dateString);

      // Validate the date
      if (!isNaN(date.getTime()) &&
          date.getDate() === parseInt(day) &&
          date.getMonth() === parseInt(month) - 1 &&
          date.getFullYear() === parseInt(year)) {
        onChange(dateString);
      }
    } else if (!day && !month && !year) {
      onChange('');
    }
  }, [day, month, year]); // Removed onChange from dependencies to prevent infinite loop

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Generate decades from 1930s to current decade
  const currentYear = new Date().getFullYear();
  const currentDecade = Math.floor(currentYear / 10) * 10;
  const decades = [];
  for (let d = currentDecade; d >= 1930; d -= 10) {
    decades.push(d);
  }

  // Generate years for the selected decade
  const getYearsForDecade = (decade) => {
    if (!decade) return [];
    const years = [];
    const maxYear = Math.min(decade + 9, currentYear);
    for (let y = maxYear; y >= decade; y--) {
      years.push(y.toString());
    }
    return years;
  };

  const yearsInDecade = selectedDecade ? getYearsForDecade(selectedDecade) : [];

  const handleDecadeSelect = (decade) => {
    setSelectedDecade(decade);
    // If there's already a year selected that's not in this decade, clear it
    if (year && (parseInt(year) < decade || parseInt(year) > decade + 9)) {
      setYear('');
    }
  };

  const handleYearSelect = (selectedYear) => {
    setYear(selectedYear);
    // Auto-set the decade if not already set
    if (!selectedDecade) {
      setSelectedDecade(Math.floor(parseInt(selectedYear) / 10) * 10);
    }
  };

  return (
    <div className="space-y-3">
      <label htmlFor={id} className="text-sm font-bold text-slate-700 block">
        Date of Birth
      </label>

      {/* Decade Quick-Jump Buttons */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-slate-600 block">Quick select decade:</label>
        <div className="flex flex-wrap gap-1.5">
          {decades.map(decade => (
            <button
              key={decade}
              type="button"
              onClick={() => handleDecadeSelect(decade)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                ${selectedDecade === decade
                  ? 'bg-violet-600 text-white shadow-md shadow-violet-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                }`}
            >
              {decade}s
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {/* Month */}
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-violet-400 text-sm bg-white text-slate-900"
          >
            <option value="">Month</option>
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Day */}
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Day</label>
          <select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-violet-400 text-sm bg-white text-slate-900"
          >
            <option value="">Day</option>
            {days.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Year</label>
          {selectedDecade ? (
            <select
              value={year}
              onChange={(e) => handleYearSelect(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-violet-400 text-sm bg-white text-slate-900"
            >
              <option value="">Year</option>
              {yearsInDecade.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          ) : (
            <div className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-400 text-sm flex items-center">
              Select decade first
            </div>
          )}
        </div>
      </div>

      {/* Direct Year Input Alternative */}
      <div className="pt-2 border-t border-slate-100">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Or enter year directly:</label>
        <input
          type="number"
          min="1930"
          max={currentYear}
          value={year}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '') {
              setYear('');
              setSelectedDecade(null);
            } else if (/^\d{0,4}$/.test(val)) {
              // Allow typing any digits up to 4 chars; only finalize when 4 digits entered
              setYear(val);
              const numVal = parseInt(val);
              if (val.length === 4 && numVal >= 1930 && numVal <= currentYear) {
                setSelectedDecade(Math.floor(numVal / 10) * 10);
              }
            }
          }}
          placeholder="e.g., 1975"
          className="w-32 h-9 px-3 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-violet-400 text-sm bg-white text-slate-900"
        />
      </div>

      {errors[id] && (
        <div className="text-red-600 text-sm flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {errors[id]}
        </div>
      )}
    </div>
  );
};

export default DateOfBirthSelector;
